import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import RankingSection from './RankingSection.jsx';

const RANKING_TABS = {
  rugby: [
    { key: 'tries', title: "MARQUEURS D'ESSAIS", shortLabel: 'Essais' },
    { key: 'scorers', title: 'RÉALISATEURS', shortLabel: 'Réalisateurs' },
    { key: 'stars', title: 'ÉTOILES', shortLabel: 'Étoiles' },
  ],
  soccer: [
    { key: 'goals', title: 'BUTEURS', shortLabel: 'Buteurs' },
    { key: 'assists', title: 'PASSEURS', shortLabel: 'Passeurs' },
    { key: 'stars', title: 'ÉTOILES', shortLabel: 'Étoiles' },
  ],
};

const getTabIndexFromStatType = (tabs, statType) => {
  const index = tabs.findIndex((tab) => tab.key === statType);
  return index === -1 ? 0 : index;
};

export default function PlayerRankingWidget({
  discipline = 'rugby',
  competitionIds = [],
  season,
  token,
  baseUrl = '',
  onNavigate,
  widgetConfigurationId=null,
  title,
  showCompetitionInfos=true,
  defaultLimit = 20,
  limitOptions = [10, 20, 50, 100],
  statType = '',
  onStatTypeChange,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const tabs = useMemo(() => RANKING_TABS[discipline] || [], [discipline]);
  const activeTab = getTabIndexFromStatType(tabs, statType);
  const currentTab = tabs[activeTab];

  const handleTabChange = (_, value) => {
    const nextStatType = tabs[value]?.key;
    if (!nextStatType) return;

    onStatTypeChange?.(nextStatType, {
      isDefaultTab: value === 0,
    });
  };

  return (
    <Box
      sx={{
        width: '100%',
        px: { xs: 1.5, sm: 2, md: 3 },
        py: { xs: 2, md: 3 },
        bgcolor: '#fff',
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Stack spacing={2.5}>
          {title && (
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: '#1f1f1f',
              }}
            >
              {title}
            </Typography>
          )}
          {showCompetitionInfos && season && (
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                color: '#1f1f1f',
              }}
            >
              Saison {season}
            </Typography>
          )}

          <Box
            sx={{
              borderBottom: '1px solid #e5e7eb',
              overflowX: 'auto',
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              TabIndicatorProps={{
                style: {
                  display: 'none',
                },
              }}
              sx={{
                minHeight: 0,
                '& .MuiTabs-flexContainer': {
                  gap: 1,
                },
              }}
            >
              {tabs.map((tab, index) => {
                const selected = index === activeTab;

                return (
                  <Tab
                    key={tab.key}
                    disableRipple
                    label={isMobile ? tab.shortLabel : tab.title}
                    sx={{
                      minHeight: 28,
                      px: 1.25,
                      py: 0.25,
                      borderRadius: '999px',
                      textTransform: 'uppercase',
                      fontWeight: 700,
                      fontSize: { xs: 10, md: 11 },
                      minWidth: 'auto',
                      color: selected ? '#111827' : '#6b7280',
                      bgcolor: selected ? '#f5f5f5' : 'transparent',
                      border: '1px solid',
                      borderColor: selected ? '#d6d6d6' : 'transparent',
                    }}
                  />
                );
              })}
            </Tabs>
          </Box>

          {currentTab && (
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                borderRadius: 3,
                borderColor: '#e5e7eb',
                overflow: 'hidden',
              }}
            >

              <Box sx={{ px: { xs: 1.5, md: 2 }, pb: 2 }}>
                <RankingSection
                  title={currentTab.title}
                  statType={currentTab.key}
                  discipline={discipline}
                  competitionIds={competitionIds}
                  season={season}
                  widgetConfigurationId={widgetConfigurationId}
                  token={token}
                  baseUrl={baseUrl}
                  limit={defaultLimit}
                  limitOptions={limitOptions}
                  sort="value_desc"
                  onNavigate={onNavigate}
                />
              </Box>
            </Paper>
          )}
        </Stack>
      </Box>
    </Box>
  );
}